import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { usePlugins } from '@/core/PluginContext';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  actions?: ActionResult[];
  isStreaming?: boolean;
}

export interface ActionResult {
  id: string;
  name: string;
  status: 'pending' | 'success' | 'error';
  data?: unknown;
  error?: string;
}

interface AISidebarContextValue {
  messages: ChatMessage[];
  isLoading: boolean;
  currentContext: PageContext;
  sendMessage: (content: string) => Promise<void>;
  executeAction: (actionId: string, params: Record<string, unknown>) => Promise<void>;
  clearHistory: () => void;
  setContext: (context: Partial<PageContext>) => void;
}

interface PageContext {
  currentPage: string;
  selectedItems: unknown[];
  pageData: Record<string, unknown>;
}

const AISidebarContext = createContext<AISidebarContextValue | null>(null);

interface AISidebarProviderProps {
  children: ReactNode;
}

export function AISidebarProvider({ children }: AISidebarProviderProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: '你好！我是 RedMix AI 助手 🍊\n\n我可以帮助你：\n- 查询和分析账号数据\n- 生成和优化内容\n- 分析竞品表现\n- 提供运营建议\n\n有什么可以帮助你的？',
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentContext, setCurrentContext] = useState<PageContext>({
    currentPage: '/',
    selectedItems: [],
    pageData: {},
  });

  const { executeAIAction, aiActions } = usePlugins();

  const generateId = () => Math.random().toString(36).substring(2, 15);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    // 添加用户消息
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // 创建助手消息占位
      const assistantMessageId = generateId();
      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isStreaming: true,
        },
      ]);

      // TODO: 调用实际的 AI 服务
      // 这里模拟一个响应
      await simulateAIResponse(content, assistantMessageId, setMessages, aiActions);
    } catch (error) {
      console.error('AI response error:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          role: 'assistant',
          content: '抱歉，处理您的请求时出现了问题。请稍后重试。',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, aiActions]);

  const executeAction = useCallback(async (actionId: string, params: Record<string, unknown>) => {
    try {
      const result = await executeAIAction(actionId, params);
      
      // 添加动作结果到消息
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          role: 'assistant',
          content: `动作执行完成`,
          timestamp: new Date(),
          actions: [
            {
              id: actionId,
              name: actionId,
              status: 'success',
              data: result,
            },
          ],
        },
      ]);
    } catch (error) {
      console.error('Action execution error:', error);
    }
  }, [executeAIAction]);

  const clearHistory = useCallback(() => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: '对话已清空。有什么可以帮助你的？',
        timestamp: new Date(),
      },
    ]);
  }, []);

  const setContext = useCallback((context: Partial<PageContext>) => {
    setCurrentContext((prev) => ({ ...prev, ...context }));
  }, []);

  const value: AISidebarContextValue = {
    messages,
    isLoading,
    currentContext,
    sendMessage,
    executeAction,
    clearHistory,
    setContext,
  };

  return (
    <AISidebarContext.Provider value={value}>
      {children}
    </AISidebarContext.Provider>
  );
}

export function useAISidebar() {
  const context = useContext(AISidebarContext);
  if (!context) {
    throw new Error('useAISidebar must be used within an AISidebarProvider');
  }
  return context;
}

// 模拟 AI 响应（实际项目中替换为真实 API 调用）
async function simulateAIResponse(
  userMessage: string,
  messageId: string,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  _aiActions: unknown[]
) {
  const responses: Record<string, string> = {
    '账号': '我可以帮你查询账号信息。你想了解哪个账号的数据？\n\n**可用操作：**\n- 查询账号列表\n- 分析账号健康度\n- 查看账号增长趋势',
    '内容': '关于内容管理，我可以帮你：\n\n1. **生成标题** - 根据主题生成多个吸引眼球的标题\n2. **优化文案** - 让文案更有感染力\n3. **分析爆款** - 分析热门内容的特点',
    '数据': '让我帮你分析数据：\n\n📊 **今日概览**\n- 总阅读量：暂无数据\n- 互动率：暂无数据\n- 新增粉丝：暂无数据\n\n需要我生成详细报告吗？',
    '竞品': '竞品分析功能可以帮你：\n\n🔍 监控竞品账号动态\n📈 对比数据表现\n📝 分析内容策略\n\n请告诉我你想分析的竞品账号。',
  };

  // 查找匹配的响应
  let response = '我理解你的问题了。让我来帮你处理...\n\n';
  for (const [keyword, resp] of Object.entries(responses)) {
    if (userMessage.includes(keyword)) {
      response = resp;
      break;
    }
  }

  if (response.startsWith('我理解')) {
    response += `你可以尝试问我：\n- "查询账号数据"\n- "帮我生成内容"\n- "分析今日数据"\n- "查看竞品表现"\n\n或者使用 \`/help\` 查看所有可用命令。`;
  }

  // 模拟流式输出
  const words = response.split('');
  let currentContent = '';

  for (const word of words) {
    currentContent += word;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, content: currentContent }
          : msg
      )
    );
    await new Promise((resolve) => setTimeout(resolve, 20));
  }

  // 完成流式输出
  setMessages((prev) =>
    prev.map((msg) =>
      msg.id === messageId
        ? { ...msg, isStreaming: false }
        : msg
    )
  );
}

